import { useEffect, useState } from 'react';
import { X, Building2, Search } from 'lucide-react';
import { SAMPLE_COMPANIES, type SampleCompany } from '../lib/sampleCompanies';

export interface CompanyContextModalProps {
  open: boolean;
  onClose: () => void;
  /** Current selection (null = none). */
  selected: Pick<SampleCompany, 'name' | 'ein'> | null;
  onApply: (company: Pick<SampleCompany, 'name' | 'ein'> | null) => void;
}

export function CompanyContextModal({
  open,
  onClose,
  selected,
  onApply,
}: CompanyContextModalProps) {
  const [draftId, setDraftId] = useState<string | 'none' | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    setSearchQuery('');
    if (!selected?.name?.trim()) {
      setDraftId(null);
      return;
    }
    const match =
      SAMPLE_COMPANIES.find((c) => c.name === selected.name && c.ein === selected.ein) ??
      SAMPLE_COMPANIES.find((c) => c.name === selected.name);
    setDraftId(match?.id ?? null);
  }, [open, selected]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleApply = () => {
    if (draftId === null) {
      onApply(null);
    } else {
      const c = SAMPLE_COMPANIES.find((x) => x.id === draftId);
      onApply(c ? { name: c.name, ein: c.ein } : null);
    }
    onClose();
  };

  const filteredCompanies = SAMPLE_COMPANIES.filter((company) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      company.name.toLowerCase().includes(query) ||
      company.ein.includes(query) ||
      company.state.toLowerCase().includes(query) ||
      company.city.toLowerCase().includes(query)
    );
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[min(85vh,28rem)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="company-context-title"
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200 flex-shrink-0">
          <div>
            <h2
              id="company-context-title"
              className="text-lg font-semibold text-slate-900 flex items-center gap-2"
            >
              <Building2 className="w-5 h-5 text-slate-600" />
              Organization context
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Tie this chat to a specific entity by name and EIN. Replies should assume you mean this
              organization unless you say otherwise.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-shrink-0 px-4 pt-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, EIN, city, or state..."
              className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent"
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-4 pb-4 space-y-2">
          {filteredCompanies.length === 0 && searchQuery.trim() && (
            <div className="text-center py-6 text-sm text-slate-500">
              No organizations found matching "{searchQuery}"
            </div>
          )}
          {filteredCompanies.map((c) => (
            <label
              key={c.id}
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-3 hover:bg-slate-50"
            >
              <input
                type="radio"
                name="company-scope"
                className="mt-1"
                checked={draftId === c.id}
                onChange={() => setDraftId(c.id)}
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800">{c.name}</div>
                <div className="text-xs text-slate-500 font-mono mt-0.5">EIN {c.ein}</div>
                <div className="text-xs text-slate-500 mt-0.5">{c.city}, {c.state}</div>
              </div>
            </label>
          ))}
        </div>

        <div className="p-4 border-t border-slate-200 flex justify-end gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="px-4 py-2 text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
